import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button, Input, Progress, Empty, Modal } from 'antd'
import { CloseOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons'
import {
  putRelacionModelo,
  putDesvincularModelo,
  postCrearModelo,
} from 'src/services/axiomaService'
import { toast } from 'react-toastify'
import './CompareMotoModelsComponent.scss'

const MAX_BULK = 20
const ROW_HEIGHT = 40
const OVERSCAN = 10

const esRespuestaOk = (response) => Boolean(response?.success)

const normalizarNombreModelo = (valor) =>
  String(valor ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ')

const parseTerminosExcel = (raw) => {
  const text = String(raw ?? '').trim()
  if (!text) return []

  // Excel columna/fila: saltos de línea o tabs.
  let parts = text
    .split(/[\n\r\t]+/)
    .map((part) => part.trim())
    .filter(Boolean)

  // Una sola línea: coma/punto y coma, o varios IDs numéricos separados por espacio
  // (p. ej. "3180 3182" al pegar desde Excel/filas).
  if (parts.length === 1) {
    const line = parts[0]
    if (/[,;]/.test(line)) {
      parts = line
        .split(/[,;]+/)
        .map((part) => part.trim())
        .filter(Boolean)
    } else {
      const tokens = line.split(/\s+/).filter(Boolean)
      if (tokens.length >= 2 && tokens.every((t) => /^\d+$/.test(t))) {
        parts = tokens
      }
    }
  }

  const seen = new Set()
  const terminos = []
  parts.forEach((term) => {
    const key = term.toUpperCase()
    if (seen.has(key)) return
    seen.add(key)
    terminos.push(term)
  })
  return terminos
}

/** Detecta búsqueda tipo "3180 3182" o "3180,3182" (varios IDs). */
const parseMultiIdsBusqueda = (raw) => {
  const tokens = String(raw ?? '')
    .trim()
    .split(/[\s,;]+/)
    .filter(Boolean)
  if (tokens.length < 2) return null
  if (!tokens.every((t) => /^\d+$/.test(t))) return null
  return tokens
}

const crearIndiceModelosPorNombre = (lista) => {
  const map = new Map()
  ;(lista || []).forEach((modelo) => {
    const key = normalizarNombreModelo(modelo?.nombre)
    if (key && !map.has(key)) map.set(key, modelo)
  })
  return map
}

const resolverModeloDesdeTermino = (termino, lista, indicePorNombre) => {
  const texto = String(termino ?? '').trim()
  if (!texto) return null

  const porNombre = indicePorNombre.get(normalizarNombreModelo(texto))
  if (porNombre) return porNombre

  if (/^\d+$/.test(texto)) {
    return (lista || []).find((modelo) => String(modelo.id) === texto) || null
  }

  return null
}

const filtrarModelosPorTerminosExcel = (lista, terminos) => {
  const indice = crearIndiceModelosPorNombre(lista)
  const vistos = new Set()
  const resultados = []

  terminos.forEach((termino) => {
    const modelo = resolverModeloDesdeTermino(termino, lista, indice)
    if (modelo && !vistos.has(modelo.id)) {
      vistos.add(modelo.id)
      resultados.push(modelo)
    }
  })

  return resultados
}

const FilaModelo = memo(function FilaModelo({
  modelo,
  seleccionado,
  vinculado,
  disabled,
  onToggle,
  onAgregar,
  onDesvincular,
}) {
  return (
    <div
      className={`modelos-fila${seleccionado ? ' is-selected' : ''}${
        vinculado ? ' is-linked' : ''
      }`}
      onClick={() => {
        if (!vinculado && !disabled) onToggle(modelo)
      }}
    >
      <input
        type="checkbox"
        className="modelos-fila__check"
        checked={seleccionado || vinculado}
        disabled={vinculado || disabled}
        onChange={() => onToggle(modelo)}
        onClick={(e) => e.stopPropagation()}
      />
      <span className="modelos-fila__meta">
        <span className="modelos-fila__nombre" title={modelo.nombre}>
          {modelo.nombre}
        </span>
        <span className="modelos-fila__id">ID {modelo.id}</span>
      </span>
      <span
        className="modelos-fila__accion"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        {vinculado ? (
          <button
            type="button"
            className="modelos-fila__btn modelos-fila__btn--danger"
            disabled={disabled}
            onClick={() => onDesvincular(modelo)}
          >
            Desvincular
          </button>
        ) : (
          <button
            type="button"
            className="modelos-fila__btn"
            disabled={disabled}
            onClick={() => onAgregar(modelo)}
          >
            Agregar
          </button>
        )}
      </span>
    </div>
  )
})

const CompareMotoModelsComponent = ({
  isReadOnly = false,
  onClose,
  onResetData,
  category,
  material,
  modelosAplicacionPrincipal,
  modelosAplicacion,
  modelosAplicacionOrigen,
  onResetAllData,
  onAddModeloAplicacion,
  onAddModeloAplicacionPrincipal,
  onRemoveModeloAplicacion,
  onRemoveModeloAplicacionPrincipal,
}) => {
  const [nombreModelo, setNombreModelo] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [busquedaDebounced, setBusquedaDebounced] = useState('')
  const [terminosFiltroExcel, setTerminosFiltroExcel] = useState([])
  const [seleccion, setSeleccion] = useState(() => ({}))
  const [bulkState, setBulkState] = useState(null)
  const [resultado, setResultado] = useState(null)
  const [listaLista, setListaLista] = useState(false)
  const [rango, setRango] = useState({ start: 0, end: 40 })
  const listRef = useRef(null)

  const esPrincipal = category === 'Modelo Aplicación Principal'
  const ocupado = Boolean(bulkState) || isReadOnly

  useEffect(() => {
    const t = setTimeout(() => setBusquedaDebounced(busqueda), 160)
    return () => clearTimeout(t)
  }, [busqueda])

  useEffect(() => {
    const id = requestAnimationFrame(() => setListaLista(true))
    return () => cancelAnimationFrame(id)
  }, [])

  const idsVinculados = useMemo(() => {
    const set = new Set()
    const lista = esPrincipal ? modelosAplicacionPrincipal : modelosAplicacion
    ;(lista || []).forEach((m) => {
      if (m?.id != null) set.add(m.id)
    })
    return set
  }, [esPrincipal, modelosAplicacion, modelosAplicacionPrincipal])

  const maxSeleccion = MAX_BULK

  const modelosFiltrados = useMemo(() => {
    if (!listaLista) return []
    const lista = Array.isArray(modelosAplicacionOrigen)
      ? modelosAplicacionOrigen
      : []

    if (terminosFiltroExcel.length > 0) {
      return filtrarModelosPorTerminosExcel(lista, terminosFiltroExcel)
    }

    const multiIds = parseMultiIdsBusqueda(busquedaDebounced)
    if (multiIds) {
      return filtrarModelosPorTerminosExcel(lista, multiIds)
    }

    const q = busquedaDebounced.trim().toLowerCase()
    if (!q) return lista
    return lista.filter((m) => {
      const nombre = String(m?.nombre || '').toLowerCase()
      return nombre.includes(q) || String(m?.id ?? '').includes(q)
    })
  }, [modelosAplicacionOrigen, busquedaDebounced, listaLista, terminosFiltroExcel])

  const countSeleccion = Object.keys(seleccion).length

  const actualizarRango = useCallback(() => {
    const el = listRef.current
    if (!el) return
    const total = modelosFiltrados.length
    const start = Math.max(0, Math.floor(el.scrollTop / ROW_HEIGHT) - OVERSCAN)
    const visible = Math.ceil(el.clientHeight / ROW_HEIGHT) + OVERSCAN * 2
    const end = Math.min(total, start + visible)
    setRango((prev) =>
      prev.start === start && prev.end === end ? prev : { start, end },
    )
  }, [modelosFiltrados.length])

  useEffect(() => {
    actualizarRango()
  }, [actualizarRango, modelosFiltrados.length])

  const toggleModelo = useCallback(
    (modelo) => {
      if (ocupado || idsVinculados.has(modelo.id)) return
      setSeleccion((prev) => {
        if (prev[modelo.id] != null) {
          const next = { ...prev }
          delete next[modelo.id]
          return next
        }
        if (Object.keys(prev).length >= maxSeleccion) {
          toast.error(`Máximo ${MAX_BULK} modelos por lote`)
          return prev
        }
        return { ...prev, [modelo.id]: modelo.nombre }
      })
      setResultado(null)
    },
    [ocupado, idsVinculados, maxSeleccion],
  )

  const quitarDeSeleccion = (id) => {
    setSeleccion((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const limpiarSeleccion = () => {
    setSeleccion({})
    setResultado(null)
  }

  const limpiarFiltroExcel = () => {
    setTerminosFiltroExcel([])
  }

  const handleBusquedaChange = (valor) => {
    setBusqueda(valor)
    if (terminosFiltroExcel.length > 0) {
      setTerminosFiltroExcel([])
    }
  }

  const handlePasteBusqueda = (event) => {
    const text = event.clipboardData?.getData('text/plain') || ''
    const terminos = parseTerminosExcel(text)
    if (terminos.length < 2) return

    event.preventDefault()
    setBusqueda('')
    setBusquedaDebounced('')
    setTerminosFiltroExcel(terminos)

    const lista = Array.isArray(modelosAplicacionOrigen)
      ? modelosAplicacionOrigen
      : []
    const indice = crearIndiceModelosPorNombre(lista)
    const coincidencias = filtrarModelosPorTerminosExcel(lista, terminos)
    const sinCoincidencia = terminos.filter(
      (termino) => !resolverModeloDesdeTermino(termino, lista, indice),
    ).length

    // Auto-selecciona los que coinciden (hasta el máximo), listos para agregar.
    const nuevaSeleccion = {}
    let omitidosVinculados = 0
    let omitidosTope = 0
    coincidencias.forEach((modelo) => {
      if (idsVinculados.has(modelo.id)) {
        omitidosVinculados += 1
        return
      }
      if (Object.keys(nuevaSeleccion).length >= maxSeleccion) {
        omitidosTope += 1
        return
      }
      nuevaSeleccion[modelo.id] = modelo.nombre
    })
    setSeleccion(nuevaSeleccion)
    setResultado(null)

    const seleccionados = Object.keys(nuevaSeleccion).length
    if (seleccionados > 0) {
      toast.info(
        `${seleccionados} modelo${seleccionados === 1 ? '' : 's'} listo${
          seleccionados === 1 ? '' : 's'
        } para agregar` +
          (sinCoincidencia > 0 ? ` · ${sinCoincidencia} sin coincidencia` : '') +
          (omitidosVinculados > 0
            ? ` · ${omitidosVinculados} ya vinculado${
                omitidosVinculados === 1 ? '' : 's'
              }`
            : '') +
          (omitidosTope > 0 ? ` · ${omitidosTope} fuera del tope` : ''),
      )
    } else if (coincidencias.length > 0) {
      toast.warn('Los modelos del Excel ya están vinculados o superan el tope')
    } else {
      toast.warn('No se encontraron modelos con esos nombres o IDs')
    }
  }

  const handleAddCategory = useCallback(
    async (modelo) => {
      if (esPrincipal) {
        const response = await putRelacionModelo({
          revisionId: material.revision_id,
          modeloId: modelo.id,
          esPrincipal: 1,
        })
        if (esRespuestaOk(response)) {
          const nuevoModelo = { id: modelo.id, nombre: modelo.nombre }
          onAddModeloAplicacionPrincipal?.(nuevoModelo)
          onAddModeloAplicacion?.(nuevoModelo)
        }
        return
      }
      const response = await putRelacionModelo({
        revisionId: material.revision_id,
        modeloId: modelo.id,
        esPrincipal: 0,
      })
      if (esRespuestaOk(response)) {
        onAddModeloAplicacion?.({ id: modelo.id, nombre: modelo.nombre })
      }
    },
    [
      esPrincipal,
      material?.revision_id,
      modelosAplicacionPrincipal,
      onAddModeloAplicacion,
      onAddModeloAplicacionPrincipal,
    ],
  )

  const handleDeleteCategory = useCallback(
    async (modelo) => {
      if (category === 'Modelo Aplicación') {
        const response = await putDesvincularModelo({
          revisionId: material.revision_id,
          modeloId: modelo.id,
          esPrincipal: 0,
        })
        if (esRespuestaOk(response)) onRemoveModeloAplicacion?.(modelo.id)
        return
      }
      const response = await putDesvincularModelo({
        revisionId: material.revision_id,
        modeloId: modelo.id,
        esPrincipal: 1,
      })
      if (esRespuestaOk(response)) {
        onRemoveModeloAplicacionPrincipal?.(modelo.id)
      }
    },
    [
      category,
      material?.revision_id,
      onRemoveModeloAplicacion,
      onRemoveModeloAplicacionPrincipal,
    ],
  )

  // Varios nombres separados por coma: cada uno es un modelo aparte. Se
  // confirman en un modal (los que ya existen se omiten) y se crean uno a uno.
  const crearModelosSeparados = (nombres) => {
    const indice = crearIndiceModelosPorNombre(modelosAplicacionOrigen)
    const nuevos = nombres.filter((n) => !indice.has(normalizarNombreModelo(n)))
    const existentes = nombres.filter((n) => indice.has(normalizarNombreModelo(n)))

    Modal.confirm({
      title: `Se crearán ${nuevos.length} modelo${nuevos.length === 1 ? '' : 's'} por separado`,
      width: 560,
      content: (
        <div>
          <p>
            El nombre tiene comas. Cada modelo se crea de forma individual, no como uno solo
            con todos los nombres:
          </p>
          <ul style={{ maxHeight: 260, overflowY: 'auto', paddingLeft: 20, margin: 0 }}>
            {nombres.map((n) => (
              <li key={n}>
                {n}
                {existentes.includes(n) && (
                  <span style={{ color: '#9ca3af' }}> · ya existe, se omite</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ),
      okText: nuevos.length ? `Crear ${nuevos.length}` : 'Entendido',
      cancelText: 'Cancelar',
      okButtonProps: { disabled: !nuevos.length },
      onOk: async () => {
        const fallidos = []
        for (let i = 0; i < nuevos.length; i += 1) {
          const response = await postCrearModelo(nuevos[i])
          if (!esRespuestaOk(response)) fallidos.push(nuevos[i])
        }
        const creados = nuevos.length - fallidos.length
        if (creados) {
          onResetData?.()
          onResetAllData?.()
          toast.success(`${creados} modelo${creados === 1 ? '' : 's'} creado${creados === 1 ? '' : 's'}`)
        }
        if (fallidos.length) {
          toast.error(`No se pudieron crear: ${fallidos.join(', ')}`)
          setNombreModelo(fallidos.join(', '))
        } else {
          setNombreModelo('')
        }
      },
    })
  }

  const handleCrearModelo = async () => {
    const nombre = nombreModelo.trim()
    if (!nombre) {
      toast.error('Escriba el nombre del modelo')
      return
    }
    const partes = nombre.split(',').map((n) => n.trim()).filter(Boolean)
    const nombres = [...new Set(partes.map(normalizarNombreModelo))]
    if (nombres.length > 1) {
      crearModelosSeparados(nombres)
      return
    }
    // Sin comas (o con una coma suelta, p. ej. al final) es un solo modelo.
    const response = await postCrearModelo(partes[0] || nombre)
    if (esRespuestaOk(response)) {
      onResetData?.()
      onResetAllData?.()
      setNombreModelo('')
      toast.success('Modelo creado exitosamente')
    } else {
      toast.error('No se pudo crear el modelo')
    }
  }

  const handleAgregarMasivo = async () => {
    if (ocupado || countSeleccion === 0) return

    const lote = Object.entries(seleccion).map(([id, nombre]) => ({
      id: Number(id),
      nombre,
    }))
    setResultado(null)
    setBulkState({ total: lote.length, actual: 0 })

    const ok = []
    const fail = []

    for (let i = 0; i < lote.length; i += 1) {
      const modelo = lote[i]
      setBulkState({ total: lote.length, actual: i + 1, nombre: modelo.nombre })
      try {
        const response = await putRelacionModelo({
          revisionId: material.revision_id,
          modeloId: modelo.id,
          esPrincipal: esPrincipal ? 1 : 0,
          silent: true,
        })
        if (esRespuestaOk(response)) {
          const nuevoModelo = { id: modelo.id, nombre: modelo.nombre }
          if (esPrincipal) {
            onAddModeloAplicacionPrincipal?.(nuevoModelo)
            onAddModeloAplicacion?.(nuevoModelo)
          } else {
            onAddModeloAplicacion?.(nuevoModelo)
          }
          ok.push(modelo)
        } else {
          fail.push({
            ...modelo,
            error: response?.error || 'No se pudo vincular',
          })
        }
      } catch (error) {
        fail.push({ ...modelo, error: error?.message || 'Error inesperado' })
      }
    }

    setBulkState(null)
    setResultado({ ok, fail })
    setSeleccion((prev) => {
      const next = { ...prev }
      ok.forEach((m) => {
        delete next[m.id]
      })
      return next
    })

    if (fail.length === 0) {
      toast.success(
        ok.length === 1
          ? '1 modelo agregado'
          : `${ok.length} modelos agregados`,
      )
    } else if (ok.length === 0) {
      toast.error('No se pudo agregar ningún modelo')
    } else {
      toast.warn(`${ok.length} agregados, ${fail.length} con error`)
    }
  }

  const progreso =
    bulkState && bulkState.total
      ? Math.round((bulkState.actual / bulkState.total) * 100)
      : 0

  const filasVisibles = listaLista
    ? modelosFiltrados.slice(rango.start, rango.end)
    : []

  return (
    <div className="modelos-overlay">
      <div className="modelos-panel">
        {bulkState && (
          <div className="modelos-loader">
            <Progress type="circle" percent={progreso} width={86} />
            <div className="modelos-loader__text">
              Agregando modelos {bulkState.actual} / {bulkState.total}
            </div>
            {bulkState.nombre && (
              <div className="modelos-loader__sub">{bulkState.nombre}</div>
            )}
          </div>
        )}

        <div className="modelos-panel__head">
          <div>
            <h2 className="modelos-panel__title">{category}</h2>
            <p className="modelos-panel__subtitle">
              Seleccione hasta {maxSeleccion} modelos. El filtro no pierde la
              selección. Pegá IDs o nombres desde Excel (columna, o varios IDs
              separados por espacio) para filtrarlos y marcarlos automáticamente.
            </p>
          </div>
          <Button
            className="modelos-panel__close"
            icon={<CloseOutlined />}
            onClick={onClose}
            disabled={Boolean(bulkState)}
          />
        </div>

        <div className="modelos-panel__toolbar">
          <Input
            className="modelos-panel__search"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Buscar por nombre o id…"
            value={busqueda}
            onChange={(e) => handleBusquedaChange(e.target.value)}
            onPaste={handlePasteBusqueda}
            disabled={ocupado}
          />
          <div className="modelos-panel__crear">
            <Input
              placeholder="Nombre del modelo nuevo"
              value={nombreModelo}
              onChange={(e) => setNombreModelo(e.target.value)}
              disabled={ocupado}
              onPressEnter={handleCrearModelo}
            />
            <Button
              icon={<PlusOutlined />}
              onClick={handleCrearModelo}
              disabled={ocupado}
            >
              Crear
            </Button>
          </div>
        </div>

        <div className="modelos-panel__bar">
          <div className="modelos-panel__bar-info">
            <span>{countSeleccion}</span> / {maxSeleccion} seleccionados
            {terminosFiltroExcel.length > 0
              ? ` · ${modelosFiltrados.length} del Excel`
              : parseMultiIdsBusqueda(busquedaDebounced)
              ? ` · ${modelosFiltrados.length} por IDs`
              : busquedaDebounced.trim()
              ? ` · ${modelosFiltrados.length} visibles`
              : ''}
          </div>
          <div className="modelos-panel__bar-actions">
            {terminosFiltroExcel.length > 0 && (
              <Button
                size="small"
                onClick={limpiarFiltroExcel}
                disabled={Boolean(bulkState)}
              >
                Quitar filtro Excel
              </Button>
            )}
            <Button
              size="small"
              onClick={limpiarSeleccion}
              disabled={countSeleccion === 0 || Boolean(bulkState)}
            >
              Limpiar
            </Button>
            <Button
              type="primary"
              onClick={handleAgregarMasivo}
              disabled={ocupado || countSeleccion === 0}
            >
              Agregar {countSeleccion || ''} seleccionados
            </Button>
          </div>
        </div>

        {countSeleccion > 0 && (
          <div className="modelos-chips">
            {Object.entries(seleccion).map(([id, nombre]) => (
              <span key={id} className="modelos-chip" title={nombre}>
                <span className="modelos-chip__text">{nombre}</span>
                {!bulkState && (
                  <button
                    type="button"
                    className="modelos-chip__x"
                    aria-label={`Quitar ${nombre}`}
                    onClick={() => quitarDeSeleccion(id)}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {resultado && (
          <div className="modelos-resultado">
            <h3>
              Resultado: {resultado.ok.length} agregados
              {resultado.fail.length
                ? `, ${resultado.fail.length} con error`
                : ''}
            </h3>
            {resultado.fail.length > 0 && (
              <ul>
                {resultado.fail.map((m) => (
                  <li key={m.id}>
                    {m.nombre}: {m.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div
          className="modelos-lista"
          ref={listRef}
          onScroll={actualizarRango}
        >
          {!listaLista ? (
            <div className="modelos-vacio">Cargando modelos…</div>
          ) : modelosFiltrados.length === 0 ? (
            <div className="modelos-vacio">
              <Empty description="No hay modelos con ese filtro" />
            </div>
          ) : (
            <div className="modelos-lista__inner">
              <div
                className="modelos-lista__pad"
                style={{ height: rango.start * ROW_HEIGHT }}
              />
              {filasVisibles.map((modelo) => (
                <FilaModelo
                  key={modelo.id}
                  modelo={modelo}
                  seleccionado={seleccion[modelo.id] != null}
                  vinculado={idsVinculados.has(modelo.id)}
                  disabled={ocupado}
                  onToggle={toggleModelo}
                  onAgregar={handleAddCategory}
                  onDesvincular={handleDeleteCategory}
                />
              ))}
              <div
                className="modelos-lista__pad"
                style={{
                  height: Math.max(
                    0,
                    (modelosFiltrados.length - rango.end) * ROW_HEIGHT,
                  ),
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CompareMotoModelsComponent
